// IMPORTANT: DO NOT REMOVE THIS FILE
import { PostMessageType } from "@/lib/utils";
import { debounce } from "lodash-es";
import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";

export interface InspectorInstance {
  disabled?: () => void;
  hideOverlay?: () => void;
}

const ID = "aippy-inspector-overlay";
const CONTAINER_ID = "aippy-inspector-container";

export const Inspector = forwardRef<InspectorInstance>((_, ref) => {
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(
    null
  );
  const selectedElementsRef = useRef<HTMLElement[]>([]);

  const overlayRef = useRef<HTMLElement | null>(null);
  const prevElementRef = useRef<HTMLElement | null>(null);

  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null);
  const contentObserverRef = useRef<MutationObserver | null>(null);

  const updateOverlay = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    overlayRef.current!.style.left = `${rect.left + window.scrollX}px`;
    overlayRef.current!.style.top = `${rect.top + window.scrollY}px`;
    overlayRef.current!.style.width = `${rect.width}px`;
    overlayRef.current!.style.height = `${rect.height}px`;
    overlayRef.current!.style.opacity = "1";
    overlayRef.current!.style.border = "1px dashed #07EE6F";
  };
  const hideOverlay = () => {
    if (overlayRef.current) {
      overlayRef.current.style.opacity = "0";
    }
  };

  const onMouseMove = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    if (!target.hasAttribute("data-aippy-id")) return;

    if (target !== hoveredElement) {
      setHoveredElement(target);
      updateOverlay(target);
    }
  };

  const debouncedOnMouseMove = useMemo(() => debounce(onMouseMove, 16), []);

  const onSelect = (
    el: HTMLElement,
    attributes: { name: string; value: string }[]
  ) => {
    const newEl = el.cloneNode(true) as HTMLElement;
    removeAippyAttributes(newEl);

    const message = {
      type: PostMessageType.selectedElement,
      elementInfo: {
        target: newEl.outerHTML,
        type: el.nodeType,
        tagName: el.tagName,
        id: getStringId(el.id),
        className: getStringClassName(el.className),
        innerHTML: el.innerHTML,
        position: mouseDownPositionRef.current,
        attributes,
      },
    };
    try {
      window.parent.postMessage(message, "*");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const notifyContentChange = (el: HTMLElement) => {
    const clonedTarget = el.cloneNode(true) as HTMLElement;

    const attributes = Array.from(clonedTarget.attributes).map((attr) => ({
      name: attr.name,
      value: attr.value,
    }));
    if (el.innerText) {
      attributes.push({ name: "data-aippy-new-content", value: el.innerText });
    }
    const message = {
      type: PostMessageType.updateContent,
      elementInfo: {
        target: clonedTarget.outerHTML,
        type: el.nodeType,
        tagName: el.tagName,
        id: getStringId(el.id),
        className: getStringClassName(el.className),
        innerHTML: el.innerHTML,
        attributes,
      },
    };
    try {
      window.parent.postMessage(message, "*");
    } catch (error) {
      console.error("Error sending content update message:", error);
    }
  };

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.target as HTMLElement;

    if (prevElementRef.current === target) return;
    if (prevElementRef.current?.contentEditable === "true") {
      prevElementRef.current.contentEditable = "false";
    }
    prevElementRef.current = target;

    if (!target.hasAttribute("data-aippy-id")) return;

    const rect = target.getBoundingClientRect();

    mouseDownPositionRef.current = {
      x: rect.left,
      y: rect.bottom,
    };

    overlayRef.current!.style.opacity = "0";
    selectedElementsRef.current.forEach((el) => {
      el.style.setProperty("outline", "none", "important");
    });

    const clonedTarget = target.cloneNode(true) as HTMLElement;

    const attributes = Array.from(clonedTarget.attributes).map((attr) => ({
      name: attr.name,
      value: attr.value,
    }));

    if (target.innerText) {
      attributes.push({ name: "innerText", value: target.innerText });
    }

    const aippyId = attributes.find(
      (attr) => attr.name === "data-aippy-id"
    )?.value;
    if (!aippyId) return;

    const elements: HTMLElement[] = Array.from(
      document.querySelectorAll(`[data-aippy-id="${aippyId}"]`)
    ) as HTMLElement[];

    // 当有多个相同id的元素时，添加动态数据标识
    if (elements.length > 1) {
      attributes.push({ name: "isDynamicData", value: "true" });
      attributes.push({
        name: "dynamicCount",
        value: elements.length.toString(),
      });
    }

    onSelect(clonedTarget, attributes);

    if (elements.length === 1) {
      target.style.setProperty("outline", "1px solid #00ff00", "important");
      selectedElementsRef.current = [target];
      // 只有当不是动态数据、有文本内容、且没有子节点时才设置为可编辑
      if (target.innerText?.trim().length > 0 && target.children.length === 0) {
        target.contentEditable = "true";
        target.focus();
        // 监听内容变动
        contentObserverRef.current?.disconnect();
        contentObserverRef.current = new MutationObserver(() => {
          notifyContentChange(target);
        });
        contentObserverRef.current.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    } else {
      // 当有多个相同id的元素时，同时显示这些元素的outline，但他们都不可编辑
      for (const el of elements) {
        el.style.setProperty("outline", "1px solid #00ff00", "important");
      }
      selectedElementsRef.current = elements;
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      disable();
      window.parent.postMessage({ type: PostMessageType.stoppedEditing }, "*");
    }
  };

  const disable = () => {
    overlayRef.current?.remove();
    window.removeEventListener("mousemove", debouncedOnMouseMove, true);
    window.removeEventListener("click", onClick, true);
    window.removeEventListener("keydown", onKeyDown);

    const container = document.getElementById(CONTAINER_ID);

    container?.remove();

    selectedElementsRef.current.forEach((el) => {
      el.style.setProperty("outline", "none", "important");
      el.style.setProperty("cursor", "", "important");
    });
    selectedElementsRef.current = [];
    setHoveredElement(null);

    contentObserverRef.current?.disconnect();
    contentObserverRef.current = null;
  };

  const enableInspector = () => {
    const container = document.createElement("div");
    container.id = CONTAINER_ID;
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      overflow: hidden;
      z-index: 999998;
    `;

    const corners = [
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
    ].map((position) => {
      const corner = document.createElement("div");
      corner.style.cssText = `
        position: absolute;
        width: 20px;
        height: 20px;
        border: 2px solid #07EE6F;
        pointer-events: none;
      `;

      switch (position) {
        case "top-left":
          corner.style.top = "4px";
          corner.style.left = "4px";
          corner.style.borderRight = "none";
          corner.style.borderBottom = "none";
          break;
        case "top-right":
          corner.style.top = "4px";
          corner.style.right = "4px";
          corner.style.borderLeft = "none";
          corner.style.borderBottom = "none";
          break;
        case "bottom-left":
          corner.style.bottom = "4px";
          corner.style.left = "4px";
          corner.style.borderRight = "none";
          corner.style.borderTop = "none";
          break;
        case "bottom-right":
          corner.style.bottom = "4px";
          corner.style.right = "4px";
          corner.style.borderLeft = "none";
          corner.style.borderTop = "none";
          break;
      }

      return corner;
    });

    corners.forEach((corner) => container.appendChild(corner));

    document.body.appendChild(container);

    overlayRef.current = document.createElement("div");
    overlayRef.current.id = ID;
    overlayRef.current.style.cssText = `
      position: absolute;
      pointer-events: none;
      z-index: 999999;
      border-radius: 4px;
      transition: width 0.2s ease-in-out, height 0.2s ease-in-out, left 0.2s ease-in-out, top 0.2s ease-in-out;
    `;
    document.body.appendChild(overlayRef.current);

    window.addEventListener("mousemove", debouncedOnMouseMove, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown);

    return disable;
  };

  useImperativeHandle(ref, () => ({ disabled: disable, hideOverlay }));

  useEffect(() => {
    enableInspector();
    return () => {
      disable();
    };
  }, []);

  return null;
});

function removeAippyAttributes(el: HTMLElement) {
  // Remove attributes from current element
  Array.from(el.attributes).forEach((attr) => {
    if (
      attr.name.startsWith("data-aippy") ||
      attr.name.startsWith("data-component")
    ) {
      el.removeAttribute(attr.name);
    }
  });

  // Recursively process all child elements
  Array.from(el.children).forEach((child) => {
    if (child instanceof HTMLElement) {
      removeAippyAttributes(child);
    }
  });
}

function getStringId(id: unknown): string {
  if (typeof id === 'string') return id;
  if (id && typeof id === 'object' && 'baseVal' in id && typeof (id as { baseVal: unknown }).baseVal === 'string') {
    return (id as { baseVal: string }).baseVal;
  }
  return '';
}

function getStringClassName(className: unknown): string {
  if (typeof className === 'string') return className;
  if (className && typeof className === 'object' && 'baseVal' in className && typeof (className as { baseVal: unknown }).baseVal === 'string') {
    return (className as { baseVal: string }).baseVal;
  }
  return '';
}
